import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Detects whether a container is "mobile-width" using ResizeObserver.
 * Unlike useMediaQuery, this responds to container width changes
 * (e.g. sidebar open/close) without viewport size changing.
 *
 * Uses a callback ref so it works even when the DOM node is
 * mounted asynchronously (e.g. inside animation wrappers).
 */
export default function useContainerMobile(breakpoint = 768) {
  const [node, setNode] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  const [width, setWidth] = useState(window.innerWidth);
  const rafRef = useRef(null);

  // Callback ref — fires when the element mounts/unmounts
  const containerRef = useCallback((el) => setNode(el), []);

  useEffect(() => {
    if (!node) return;

    const measure = () => {
      const w = node.getBoundingClientRect().width;
      if (w > 0) {
        setWidth(w);
        setIsMobile((prev) => {
          const next = w < breakpoint;
          return prev === next ? prev : next;
        });
      }
    };

    measure();

    const observer = new ResizeObserver(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [node, breakpoint]);

  return { containerRef, isMobile, width };
}
